import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ExternalLink, Check, Clock, GraduationCap, Sparkles, StickyNote, FileText, PlayCircle, Headphones, Link as LinkIcon, Download, AlertTriangle, Pencil, Trash2, MoveHorizontal } from "lucide-react";
import { RunnerDialog, ResultDialog } from "./AssessmentsPanel";
import "./AssessmentsPanel.css";

/** Inline Edit form for a resource — mirrors the fields exposed by
 *  Concept2Modal's QuickForm so users can rename, retype, relink and
 *  re-level a resource without leaving the viewer. */
const EditResourcePanel = ({ resource, onClose, onSaved }) => {
    const [name, setName] = useState(resource.name || "");
    const [type, setType] = useState((resource.type || "article").toLowerCase());
    const [url, setUrl] = useState(resource.url || "");
    const [level, setLevel] = useState((resource.level || "beginner").toLowerCase());
    const [duration, setDuration] = useState(resource.duration_minutes || "");
    const [notes, setNotes] = useState(resource.notes || "");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        const n = name.trim();
        if (!n) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const r = await api.put(`/learning/resources/${resource.id}`, {
                name: n,
                type: type || "article",
                url: url || "",
                level: level || "beginner",
                duration_minutes: duration ? Number(duration) : null,
                notes: notes || "",
            });
            toast.success("Resource updated");
            onSaved?.(r.data);
        } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="lh-c2-vw-inline-backdrop" onMouseDown={onClose}>
            <form className="lh-c2-vw-inline-card" onSubmit={submit} onMouseDown={(e) => e.stopPropagation()} data-testid="lh-c2-vw-edit-form">
                <h3>Edit resource</h3>
                <label>Name<input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="lh-c2-vw-edit-name" /></label>
                <div className="lh-c2-vw-inline-row">
                    <label>Type
                        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="lh-c2-vw-edit-type">
                            <option value="article">Article</option>
                            <option value="video">Video</option>
                            <option value="podcast">Podcast</option>
                            <option value="book">Book</option>
                            <option value="quiz">Quiz</option>
                            <option value="doc">Doc</option>
                            <option value="note">Note</option>
                        </select>
                    </label>
                    <label>Level
                        <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="lh-c2-vw-edit-level">
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>
                    </label>
                    <label>Duration (min)<input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="lh-c2-vw-edit-duration" /></label>
                </div>
                <label>URL<input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="lh-c2-vw-edit-url" /></label>
                <label>Notes<textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lh-c2-vw-edit-notes" /></label>
                <div className="lh-c2-vw-inline-actions">
                    <button type="button" onClick={onClose} className="lh-c2m-tbtn" data-testid="lh-c2-vw-edit-cancel">Cancel</button>
                    <button type="submit" disabled={busy} className="lh-c2m-tbtn" style={{ background: "var(--navy)", color: "#fff" }} data-testid="lh-c2-vw-edit-save">
                        {busy ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </form>
        </div>
    );
};

/** Inline Move panel — fetches every collection the user can reach in
 *  the current resource's owning department and lets them retarget the
 *  resource to a new collection. */
const MoveResourcePanel = ({ resource, collection, onClose, onMoved }) => {
    const [targets, setTargets] = useState([]);
    const [target, setTarget] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/learning/collections");
                const dept = collection?.owner_department_id;
                const opts = (r.data || []).filter((c) => c.id !== collection?.id && (!dept || c.owner_department_id === dept));
                setTargets(opts);
            } catch { setTargets([]); }
        })();
    }, [collection]);
    const submit = async (e) => {
        e.preventDefault();
        if (!target) { toast.error("Pick a target collection"); return; }
        setBusy(true);
        try {
            await api.post(`/learning/resources/${resource.id}/move`, { collection_id: target });
            toast.success("Resource moved");
            const targetCol = targets.find((c) => c.id === target);
            onMoved?.(targetCol);
        } catch (err) { toast.error(err?.response?.data?.detail || "Move failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="lh-c2-vw-inline-backdrop" onMouseDown={onClose}>
            <form className="lh-c2-vw-inline-card" onSubmit={submit} onMouseDown={(e) => e.stopPropagation()} data-testid="lh-c2-vw-move-form">
                <h3>Move resource</h3>
                <p className="lh-c2-vw-inline-help">Move &ldquo;{resource.name}&rdquo; to a different collection in the same department.</p>
                <label>Target collection
                    <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="lh-c2-vw-move-target">
                        <option value="">— Pick a collection —</option>
                        {targets.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </label>
                {targets.length === 0 && (
                    <p className="lh-c2-vw-inline-help" style={{ color: "#C6543F" }}>No other collections in this department yet.</p>
                )}
                <div className="lh-c2-vw-inline-actions">
                    <button type="button" onClick={onClose} className="lh-c2m-tbtn" data-testid="lh-c2-vw-move-cancel">Cancel</button>
                    <button type="submit" disabled={busy || !target} className="lh-c2m-tbtn" style={{ background: "var(--navy)", color: "#fff" }} data-testid="lh-c2-vw-move-save">
                        {busy ? "Moving…" : "Move"}
                    </button>
                </div>
            </form>
        </div>
    );
};

/** Decide the "best-fit" viewer strategy. Only content that reliably renders
 *  inside an iframe/<video>/<audio> tag gets embedded — everything else
 *  (Google Docs/Slides, PPT/DOC/XLSX uploads, generic articles) falls back to
 *  the "Open in a new tab" CTA card, because embedding those either strips
 *  functionality (Docs) or blows up the ingress with a 502 (large PPTX). */
const chooseKind = (r) => {
    const url = (r?.url || "").toLowerCase();
    const t = (r?.type || "").toLowerCase();
    const ct = (r?.content_type || "").toLowerCase();
    // Video — direct file, MIME hint, or explicit type
    if (t === "video" || ct.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(url)) return "video";
    // Audio — direct file, MIME hint, or explicit type
    if (t === "podcast" || t === "audio" || ct.startsWith("audio/") || /\.(mp3|wav|ogg)$/i.test(url)) return "audio";
    // Real PDFs only — DOC/PPT/XLSX would return raw bytes to a browser
    if (ct === "application/pdf" || url.endsWith(".pdf")) return "pdf";
    // Known embed hosts
    if (/youtube\.com|youtu\.be|vimeo\.com|loom\.com/.test(url)) return "iframe";
    // Anything else → CTA card. Never blindly iframe an arbitrary URL —
    // Google Docs/Slides refuses to render, and Office file MIME types
    // simply download in the parent tab.
    return "article";
};

const youtubeEmbed = (url) => {
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
    return url;
};

/** Copy that adapts to the resource type so the CTA reads naturally. */
const openCopy = (r, kind) => {
    switch (kind) {
        case "video": return { verb: "Watch video", icon: PlayCircle };
        case "audio": return { verb: "Play audio", icon: Headphones };
        case "pdf":   return { verb: "Open PDF",   icon: FileText };
        case "article": return { verb: "Open article", icon: ExternalLink };
        default: return { verb: r?.storage_path ? "Open file" : "Open source", icon: ExternalLink };
    }
};

/** Concept-2 in-browser viewer — full modal.
 *
 *  Left rail:  player / iframe / article body.
 *  Right rail: rich detail block — overview, learning outcomes, meta
 *              chips (duration + difficulty), extra notes.
 *  Footer:     "Mark as done" toggle + a concept-2 primary button that
 *              opens the source in a new tab (matches the hub button
 *              language — no more raw <a> chip). */
export const Concept2Viewer = ({ resource, collection, onClose, onStatusChange, onEditResource, onMoveResource, onDeleteResource, onResourceUpdated, canManage = false }) => {
    const [done, setDone] = useState(resource?.my_status === "completed");
    const [articleBody, setArticleBody] = useState(null);
    // Inline edit / move panels rendered on top of the viewer.
    const [showEdit, setShowEdit] = useState(false);
    const [showMove, setShowMove] = useState(false);

    useEffect(() => { setDone(resource?.my_status === "completed"); }, [resource?.id, resource?.my_status]);
    useEffect(() => {
        if (chooseKind(resource) === "article" && !resource.storage_path) {
            setArticleBody({
                title: resource.name,
                body: resource.description || (resource.overview || "Click the source button to read the full article."),
            });
        }
    }, [resource]);

    const kind = useMemo(() => chooseKind(resource), [resource]);
    const src = resource?.url || "";
    const isStorage = !!resource?.storage_path;
    const storageSrc = isStorage ? `${process.env.REACT_APP_BACKEND_URL}/api/learning/files/${resource.storage_path.split("/").map(encodeURIComponent).join("/")}` : null;

    const toggleDone = async () => {
        const next = !done;
        setDone(next);
        try {
            await api.put(`/learning/resources/${resource.id}/status`, { status: next ? "completed" : "not_started" });
            onStatusChange?.(next);
        } catch { toast.error("Couldn't update status"); setDone(!next); }
    };

    if (!resource) return null;
    const outcomes = Array.isArray(resource.learning_outcomes) ? resource.learning_outcomes.filter(Boolean) : [];
    const hasDetail = resource.overview || outcomes.length || resource.notes || resource.duration_minutes || resource.level;

    /* Build the list of CTAs upfront so a resource with BOTH a URL and
     * an uploaded file renders both side-by-side. Order: link first
     * (usually authoritative), then file. */
    const ctas = [];
    if (src) {
        ctas.push({
            key: "url",
            href: src,
            eyebrow: "External link",
            title: `${openCopy(resource, kind).verb} in a new tab`,
            sub: (() => { try { return new URL(src).hostname.replace(/^www\./, ""); } catch { return src; } })(),
            Icon: openCopy(resource, kind).icon,
        });
    }
    if (isStorage) {
        ctas.push({
            key: "file",
            href: storageSrc,
            eyebrow: "Uploaded file",
            title: `Open ${resource.content_type?.startsWith("video/") ? "video" : resource.content_type?.startsWith("audio/") ? "audio" : resource.content_type === "application/pdf" ? "PDF" : "file"} in a new tab`,
            sub: resource.file_original_name || resource.name,
            Icon: FileText,
        });
    }
    const hasMediaPreview = ["video", "audio", "pdf", "iframe"].includes(kind);
    /* CTA layout: media-preview kinds keep the embedded player. Article/
     *   doc/note kinds render the CTA card(s) — two side-by-side when both
     *   a URL and a file are attached, one full-width otherwise. */

    return createPortal(
        <div className="lh-c2-viewer-backdrop" onClick={onClose} data-testid="lh-c2-viewer-backdrop">
            <div className="lh-c2-viewer" onClick={(e) => e.stopPropagation()} data-testid={`lh-c2-viewer-${resource.id}`}>
                <div className="lh-c2-viewer-head">
                    <div>
                        <div className="lh-c2-viewer-crumb">
                            {collection?.name || "Loose resource"} · {(resource.type || "").toUpperCase()}
                            {resource.storage_missing && (
                                <span className="lh-c2-missing-pill" data-testid="lh-c2-vw-missing-pill" title="This file is missing from storage — re-upload it via Edit.">
                                    <AlertTriangle size={10} /> File missing — re-upload
                                </span>
                            )}
                        </div>
                        <h3>{resource.name}</h3>
                    </div>
                    <div className="lh-c2-viewer-head-actions">
                        {canManage && (
                            <button className="lh-c2m-tbtn" onClick={() => setShowEdit(true)} data-testid="lh-c2-vw-edit"><Pencil size={13} /> Edit</button>
                        )}
                        {canManage && (
                            <button className="lh-c2m-tbtn" onClick={() => setShowMove(true)} data-testid="lh-c2-vw-move"><MoveHorizontal size={13} /> Move</button>
                        )}
                        {canManage && onDeleteResource && (
                            <button className="lh-c2m-tbtn lh-c2m-tbtn-danger" onClick={() => onDeleteResource(resource)} data-testid="lh-c2-vw-delete"><Trash2 size={13} /> Delete</button>
                        )}
                        <button className="lh-c2-modal-close" onClick={onClose} aria-label="Close" data-testid="lh-c2-viewer-close">
                            <X size={16} />
                        </button>
                    </div>
                </div>
                <div className="lh-c2-viewer-body lh-c2-viewer-stack">
                    <div className="lh-c2-viewer-media">
                        {kind === "video" && (
                            <div className="lh-c2-vw-player">
                                {(isStorage || /\.(mp4|webm|mov)$/i.test(src)) ? (
                                    <video controls src={storageSrc || src} data-testid="lh-c2-vw-video" />
                                ) : (
                                    <iframe title={resource.name} src={youtubeEmbed(src)} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="lh-c2-vw-video-iframe" />
                                )}
                            </div>
                        )}
                        {kind === "audio" && (
                            <div className="lh-c2-vw-audio" data-testid="lh-c2-vw-audio">
                                <audio controls src={storageSrc || src} style={{ flex: 1 }} />
                            </div>
                        )}
                        {kind === "pdf" && (
                            <iframe title={resource.name} src={storageSrc || src} className="lh-c2-vw-pdf" data-testid="lh-c2-vw-pdf" />
                        )}
                        {kind === "iframe" && (
                            <iframe title={resource.name} src={storageSrc || src} className="lh-c2-vw-pdf" sandbox="allow-scripts allow-same-origin allow-forms allow-popups" data-testid="lh-c2-vw-iframe" />
                        )}
                        {!hasMediaPreview && ctas.length > 0 && (
                            <div className={`lh-c2-vw-cta-grid ${ctas.length > 1 ? "two-up" : ""}`} data-testid="lh-c2-vw-cta-grid">
                                {ctas.map((c) => (
                                    <a
                                        key={c.key}
                                        href={c.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="lh-c2-vw-cta"
                                        data-testid={`lh-c2-vw-cta-${c.key}`}
                                    >
                                        <div className="lh-c2-vw-cta-icon"><c.Icon size={26} /></div>
                                        <div className="lh-c2-vw-cta-copy">
                                            <div className="lh-c2-vw-cta-eyebrow"><LinkIcon size={11} /> {c.eyebrow}</div>
                                            <div className="lh-c2-vw-cta-title">{c.title}</div>
                                            <div className="lh-c2-vw-cta-sub">{c.sub}</div>
                                        </div>
                                        <div className="lh-c2-vw-cta-go"><ExternalLink size={16} /></div>
                                    </a>
                                ))}
                            </div>
                        )}
                        {!hasMediaPreview && ctas.length === 0 && articleBody && (
                            <div className="lh-c2-vw-article" data-testid="lh-c2-vw-article">
                                <h1>{articleBody.title}</h1>
                                <p>{articleBody.body}</p>
                            </div>
                        )}
                    </div>
                    {hasDetail && (
                        <section className="lh-c2-viewer-detail" data-testid="lh-c2-viewer-side">
                            <ResourceAssessmentBanner resource={resource} collection={collection} />
                            <div className="lh-c2-viewer-chips">
                                {resource.duration_minutes ? (
                                    <span className="lh-c2-viewer-chip"><Clock size={12} /> {resource.duration_minutes} min</span>
                                ) : null}
                                {resource.level ? (
                                    <span className="lh-c2-viewer-chip"><GraduationCap size={12} /> {resource.level[0].toUpperCase() + resource.level.slice(1)}</span>
                                ) : null}
                            </div>
                            <div className="lh-c2-viewer-detail-grid">
                                {resource.overview && (
                                    <section className="lh-c2-viewer-detail-block">
                                        <h4><Sparkles size={13} /> Overview</h4>
                                        <p>{resource.overview}</p>
                                    </section>
                                )}
                                {outcomes.length > 0 && (
                                    <section className="lh-c2-viewer-detail-block">
                                        <h4><Check size={13} /> What you'll learn</h4>
                                        <ul>
                                            {outcomes.map((o, i) => <li key={i}>{o}</li>)}
                                        </ul>
                                    </section>
                                )}
                                {resource.notes && (
                                    <section className="lh-c2-viewer-detail-block">
                                        <h4><StickyNote size={13} /> Notes</h4>
                                        <p>{resource.notes}</p>
                                    </section>
                                )}
                            </div>
                        </section>
                    )}
                </div>
                <div className="lh-c2-viewer-footer">
                    <label className="lh-c2-vw-check" data-testid="lh-c2-vw-check-label">
                        <input type="checkbox" checked={done} onChange={toggleDone} data-testid="lh-c2-vw-check" />
                        {done ? "Marked done" : "Mark as done"}
                    </label>
                    <div className="lh-c2-viewer-footer-actions">
                        {src && (
                            <a
                                href={src}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lh-c2-btn lh-c2-btn-solid lh-c2-vw-open-btn"
                                data-testid="lh-c2-vw-open-source"
                            >
                                <ExternalLink size={13} /> Open link
                            </a>
                        )}
                        {isStorage && (
                            <a
                                href={storageSrc}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`lh-c2-btn ${src ? "" : "lh-c2-btn-solid"} lh-c2-vw-open-btn`}
                                data-testid="lh-c2-vw-open-file"
                            >
                                <FileText size={13} /> Open file
                            </a>
                        )}
                    </div>
                </div>
                {showEdit && (
                    <EditResourcePanel
                        resource={resource}
                        onClose={() => setShowEdit(false)}
                        onSaved={(updated) => { setShowEdit(false); onResourceUpdated?.(updated); onEditResource?.(updated); }}
                    />
                )}
                {showMove && (
                    <MoveResourcePanel
                        resource={resource}
                        collection={collection}
                        onClose={() => setShowMove(false)}
                        onMoved={(target) => { setShowMove(false); onMoveResource?.(resource, target); onClose?.(); }}
                    />
                )}
            </div>
        </div>,
        document.body
    );
};

export default Concept2Viewer;

/* ── Resource-scoped assessment banner — shows every assessment whose
 *   scope = this exact resource. Collapses to nothing when there are
 *   no resource-scoped assessments so viewers stay quiet by default. */
const ResourceAssessmentBanner = ({ resource, collection }) => {
    const [list, setList] = useState([]);
    const [runner, setRunner] = useState(null);
    const [result, setResult] = useState(null);
    useEffect(() => {
        if (!resource?.id) return;
        api.get(`/learning/assessments?resource_id=${resource.id}`).then((r) => setList(r.data || [])).catch(() => {});
    }, [resource?.id]);
    if (!list.length) return null;
    return (
        <>
            {list.filter((a) => a.is_published || a.created_by).map((a) => {
                const my = a.my_attempt;
                const canTake = a.is_published && (my?.status !== "graded" || a.max_attempts === 0);
                return (
                    <div key={a.id} className="asm-viewer-banner" data-testid={`asm-viewer-banner-${a.id}`}>
                        <div className="asm-viewer-banner-main">
                            <div className="asm-viewer-banner-eyebrow">▰ ASSESSMENT ON THIS RESOURCE</div>
                            <div className="asm-viewer-banner-title">{a.title}</div>
                            <div className="asm-viewer-banner-meta">
                                {a.question_count || 0} Q · pass {a.pass_threshold}% · {a.time_limit_minutes ? `${a.time_limit_minutes} min` : "untimed"}
                                {my?.status === "graded" && ` · you scored ${Math.round(my.percentage ?? ((my.score / Math.max(1, my.max_score)) * 100))}%`}
                            </div>
                        </div>
                        <div className="asm-viewer-banner-cta">
                            {my?.status === "graded" ? (
                                <button type="button" className="asm-btn asm-btn-ghost" onClick={() => setResult(my.id)}>View result</button>
                            ) : canTake ? (
                                <button type="button" className="asm-btn asm-btn-primary" onClick={() => setRunner(a.id)} data-testid={`asm-viewer-take-${a.id}`}>
                                    {my?.status === "in_progress" ? "Resume" : "Take now"} →
                                </button>
                            ) : null}
                        </div>
                    </div>
                );
            })}
            {runner && (
                <RunnerDialog
                    aid={runner}
                    onClose={() => setRunner(null)}
                    onSubmitted={(att) => { setRunner(null); setResult(att.id); api.get(`/learning/assessments?resource_id=${resource.id}`).then((r) => setList(r.data || [])); }}
                />
            )}
            {result && (
                <ResultDialog
                    attemptId={result}
                    onClose={() => setResult(null)}
                    onRetry={() => {
                        // Find the assessment behind this attempt and open the runner on it
                        const match = list.find((a) => a.my_attempt?.id === result);
                        setResult(null);
                        if (match) setRunner(match.id);
                    }}
                />
            )}
        </>
    );
};
